import { AdminLayout } from "components/admin/admin-layout";
import {
  useGetAllBooksForAdminPanelQuery,
  Books_Order_By,
  Order_By,
  Books_Bool_Exp,
  GetAllBooksForAdminPanelQueryVariables,
} from "lib/graphql-generated";
import { gql } from "@apollo/client";
import { Button } from "components/buttons/button";
import { useRouter } from "next/router";
import { MouseEvent, useRef, useState } from "react";
import Link from "next/link";
import currency from "currency.js";
import { AdminSearchBar } from "components/admin/admin-search-bar";
import { useInput } from "lib/hooks/use-input";
import { parseParam } from "lib/utils";
import querystring from "querystring";

const GET_ALL_BOOKS = gql`
  query GetAllBooksForAdminPanel(
    $limit: Int
    $offset: Int
    $orderBy: [books_order_by!]
    $conditions: books_bool_exp
  ) {
    books_aggregate(where: $conditions) {
      aggregate {
        count
      }
    }
    books(
      limit: $limit
      offset: $offset
      order_by: $orderBy
      where: $conditions
    ) {
      isbn
      title
      author
      stock
      price
      created_at
      is_unlisted
      track_stock
    }
  }
`;

export const Books = () => {
  /**
   * Query Strings
   */
  const router = useRouter();
  const pQuery = parseInt(router.query["p"] as string);
  const paginate = Boolean(pQuery) ? pQuery : 0;
  const limit = 25;
  const offset = Boolean(paginate) ? paginate * limit : 0;
  const searchQuery = parseParam(router.query["search"] || "");
  const search = useInput<string>(searchQuery);
  const orderByt = parseParam(router.query["order"]);
  const orderDir = parseParam(router.query["dir"]);

  /**
   * Form Fields
   */
  const file = useRef(null);
  const [hasFile, setHasFile] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>(null);
  const [uploadSucces, setUploadSucces] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [orderBy, setOrderBy] = useState<Books_Order_By[]>([
    { created_at: Order_By.Desc },
    { title: Order_By.Asc },
  ]);

  const getPathWithQuery = ({
    p,
    search,
    order,
    dir,
  }: {
    p?: number;
    search?: string;
    order?: string;
    dir?: string;
  } = {}) => {
    const queryString = querystring.encode({
      p: p ?? paginate ?? 0,
      search: search ?? searchQuery ?? "",
      order: order ?? orderByt ?? "",
      dir: dir ?? orderDir ?? "",
    });
    return `${router.pathname}?${queryString}`;
  };

  let variables: GetAllBooksForAdminPanelQueryVariables = {
    limit: limit,
    offset: offset,
    orderBy:
      orderByt === "" || orderDir === ""
        ? [{ created_at: Order_By.Desc }]
        : orderByt === "created_at"
        ? [{ created_at: orderDir as Order_By }]
        : [
            { [orderByt]: orderDir } as Books_Order_By,
            { created_at: Order_By.Desc },
          ],
    conditions: {
      _or: [
        { title: { _ilike: `%${searchQuery}%` } },
        { author: { _ilike: `%${searchQuery}%` } },
      ] as Books_Bool_Exp["_or"],
    },
  };

  if (parseInt(searchQuery)) {
    variables.conditions._or.push({
      isbn: { _eq: parseInt(searchQuery) },
    });
  }

  if (searchQuery.startsWith("t:")) {
    variables.conditions._or.push({
      tag_books: {
        tag: { text: { _ilike: `%${searchQuery.slice(2).trim()}%` } },
      },
    });
    console.log(variables);
  }

  const { data, loading, error, refetch } = useGetAllBooksForAdminPanelQuery({
    variables: variables,
    fetchPolicy: "cache-and-network",
  });

  const handleUpload = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("file", file.current.files[0]);
    setUploading(true);
    setUploadError(null);

    fetch("/api/upload-books", {
      method: "POST",
      headers: {
        authorization: `Bearer ${localStorage.getItem("jwt")}`,
      },
      body: formData,
    })
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data.error) {
          setUploadError(data.error.message);
          setUploading(false);
        } else if (data.success) {
          setUploadSucces(true);
          setHasFile(false);
          setUploading(false);
          refetch();
        }
      })
      .catch((e) => console.error(e));
  };

  return (
    <>
      <AdminLayout
        page='Books'
        searchBar={
          <AdminSearchBar
            placeholder='Search Title / ISBN / Author'
            onSearch={() =>
              router.replace(getPathWithQuery({ search: search.value, p: 0 }))
            }
            {...search.bind}
          />
        }>
        <article className='space-y-4'>
          <header className='flex flex-col xl:flex-row flex-wrap w-full justify-center space-y-4 xl:space-y-0 xl:space-x-4'>
            <h1 className='header text-4xl ml-auto mr-auto xl:ml-0 xl:mr-auto'>
              Books
            </h1>
            {uploadError && (
              <aside className='border rounded border-red-default bg-red-50 flex flex-col content-center px-2'>
                <p className='m-auto text-red-900'>{uploadError}</p>
              </aside>
            )}
            <form
              action='/api/upload-books'
              method='POST'
              className='border border-yellow-default bg-white rounded px-4 py-2 space-x-2 ml-auto mr-auto'>
              <input
                required
                ref={file}
                aria-labelledby='uploadButton'
                type='file'
                name='upload'
                id='upload'
                className='max-w-md overflow-x-hidden truncate'
                onChange={(e) =>
                  setHasFile(Boolean(file.current?.files[0].name))
                }
              />

              <Button
                loading={uploading}
                onClick={handleUpload}
                type='submit'
                id='uploadButton'
                buttonType='primary'
                buttonSize='sm'
                disabled={uploading || !hasFile}
                className='my-auto'>
                {uploadSucces ? "Upload Succesful!" : "Upload CSV"}
              </Button>
            </form>
            <Button
              buttonType='secondary'
              buttonSize='lg'
              href='/admin/books/new'
              className='my-auto ml-auto mr-auto'>
              <svg
                className='w-4 h-4 mr-2 -ml-1 mb-1'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
              <span>New Book</span>
            </Button>
          </header>

          <table className='min-w-full divide-y divide-gray-200 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg'>
            <thead className=' bg-gray-100 text-left text-xs leading-4 font-medium text-gray-700 uppercase tracking-wider cursor-pointer'>
              <tr>
                <th
                  className={`px-6 py-3 hover:bg-gray-200 ${
                    orderByt === "title" && "bg-gray-200"
                  }`}
                  onClick={() => {
                    router.replace(
                      getPathWithQuery({
                        order: "title",
                        dir:
                          orderDir === "asc"
                            ? "desc"
                            : orderDir === "desc"
                            ? ""
                            : "asc",
                      })
                    );
                  }}>
                  Title{" "}
                  {orderDir === "asc" && orderByt === "title" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : orderDir === "desc" && orderByt === "title" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    ""
                  )}
                </th>
                <th
                  className={`px-6 py-3 hover:bg-gray-200 hidden xl:table-cell ${
                    orderByt === "author" && "bg-gray-200"
                  }`}
                  onClick={() => {
                    router.replace(
                      getPathWithQuery({
                        order: "author",
                        dir:
                          orderDir === "asc"
                            ? "desc"
                            : orderDir === "desc"
                            ? ""
                            : "asc",
                      })
                    );
                  }}>
                  Author{" "}
                  {orderDir === "asc" && orderByt === "author" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : orderDir === "desc" && orderByt === "author" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    ""
                  )}
                </th>
                <th
                  className={`px-6 py-3 hover:bg-gray-200 ${
                    orderByt === "stock" && "bg-gray-200"
                  }`}
                  onClick={() => {
                    router.replace(
                      getPathWithQuery({
                        order: "stock",
                        dir:
                          orderDir === "asc"
                            ? "desc"
                            : orderDir === "desc"
                            ? ""
                            : "asc",
                      })
                    );
                  }}>
                  Stock{" "}
                  {orderDir === "asc" && orderByt === "stock" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : orderDir === "desc" && orderByt === "stock" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    ""
                  )}
                </th>
                <th
                  className={`px-6 py-3 hover:bg-gray-200 hidden xl:table-cell ${
                    orderByt === "price" && "bg-gray-200"
                  }`}
                  onClick={() => {
                    router.replace(
                      getPathWithQuery({
                        order: "price",
                        dir:
                          orderDir === "asc"
                            ? "desc"
                            : orderDir === "desc"
                            ? ""
                            : "asc",
                      })
                    );
                  }}>
                  Price{" "}
                  {orderDir === "asc" && orderByt === "price" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : orderDir === "desc" && orderByt === "price" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    ""
                  )}
                </th>
                <th
                  className={`px-6 py-3 hover:bg-gray-200 hidden xl:table-cell ${
                    orderByt === "created_at" && "bg-gray-200"
                  }`}
                  onClick={() => {
                    router.replace(
                      getPathWithQuery({
                        order: "created_at",
                        dir:
                          orderDir === "asc"
                            ? "desc"
                            : orderDir === "desc"
                            ? ""
                            : "asc",
                      })
                    );
                  }}>
                  Added on{" "}
                  {orderDir === "asc" && orderByt === "created_at" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : orderDir === "desc" && orderByt === "created_at" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    ""
                  )}
                </th>
                <th
                  className={`px-6 py-3 hover:bg-gray-200 hidden md:table-cell ${
                    orderByt === "isbn" && "bg-gray-200"
                  }`}
                  onClick={() => {
                    router.replace(
                      getPathWithQuery({
                        order: "isbn",
                        dir:
                          orderDir === "asc"
                            ? "desc"
                            : orderDir === "desc"
                            ? ""
                            : "asc",
                      })
                    );
                  }}>
                  ISBN{" "}
                  {orderDir === "asc" && orderByt === "isbn" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : orderDir === "desc" && orderByt === "isbn" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    ""
                  )}
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200 text-sm text-gray-800'>
              {loading ? (
                <tr className='hover:bg-gray-300'>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={6}>
                    Loading Data...
                  </td>
                </tr>
              ) : data?.books.length !== 0 ? (
                data?.books.map((book) => (
                  <tr
                    key={book.isbn}
                    className={`hover:bg-gray-300 cursor-pointer`}>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {book.is_unlisted && (
                        <span className='bg-red-50 text-red-default rounded text-sm px-2 mr-1'>
                          unlisted
                        </span>
                      )}{" "}
                      <Link href={`/admin/books/${book.isbn}`}>
                        <a className='hover:underline'>{book.title}</a>
                      </Link>
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden xl:table-cell'>
                      {book.author}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5 text-right'>
                      {book.stock === null || !book.track_stock
                        ? "N/A"
                        : book.stock}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden xl:table-cell text-right'>
                      {currency(book.price, { fromCents: true }).value}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden xl:table-cell text-right'>
                      {new Date(book.created_at).toLocaleDateString("en-GB")}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5 hidden md:table-cell'>
                      {book.isbn}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className='hover:bg-gray-300'>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={6}>
                    Could not find books...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {data && (
            <nav
              className='py-3 flex items-center justify-between border-t border-gray-200 '
              aria-label='Pagination'>
              <div className='hidden sm:block'>
                <p className='text-sm text-gray-700'>
                  Showing{" "}
                  <span className='font-medium'>
                    {offset}
                    {` `}
                  </span>
                  to{" "}
                  <span className='font-medium'>
                    {offset + limit}
                    {` `}
                  </span>
                  of{" "}
                  <span className='font-medium'>
                    {data?.books_aggregate.aggregate.count}
                    {` `}
                  </span>
                  results
                </p>
              </div>
              <div className='flex-1 flex justify-between sm:justify-end'>
                {paginate !== 0 && (
                  <Link href={getPathWithQuery({ p: paginate - 1 })} shallow>
                    <a className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                      Previous
                    </a>
                  </Link>
                )}
                {paginate !==
                  Math.floor(data.books_aggregate.aggregate.count / 25) && (
                  <Link href={getPathWithQuery({ p: paginate + 1 })} shallow>
                    <a className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                      Next
                    </a>
                  </Link>
                )}
              </div>
            </nav>
          )}
        </article>
      </AdminLayout>
    </>
  );
};

export default Books;
